<template>
  <div v-if="!hideLegend">
    <v-card class="no-bottom-border-radius legend-card">
      <v-card-subtitle
        class="d-flex align-center card-subtitle opacity-100"
        @click="toggle"
        data-cy="mapLegendToggle"
      >
        <div>
          {{ legendTitle }}
        </div>
        <v-spacer></v-spacer>
        <div>
          <v-icon v-show="!open">mdi-chevron-up</v-icon>
          <v-icon v-show="open">mdi-chevron-down</v-icon>
        </div>
      </v-card-subtitle>
    </v-card>
    <v-expand-transition>
      <v-card
        class="bottom-card no-top-border-radius no-bottom-border-radius"
        flat
        v-show="open"
        data-cy="mapLegendExpanded"
      >
        <div class="subtitle-large hide-large">
          {{ $t('map.legend.levels') }}
        </div>
        <div class="legend">
          <div class="subtitle hide-small">
            {{ $t('map.legend.levels') }}
          </div>
          <div
            v-for="item in legendItems"
            :key="item.name"
            :class="['entry', item.name]"
            :data-cy="`legendEntry-${item.level}`"
          >
            <div
              class="category-subtitle d-flex flex-column justify-end"
              :data-cy="`legendEntryTitle-${item.level}`"
            >
              <span>{{ getLegendTitle(item.level) }}</span>
            </div>
            <div
              class="range"
              :style="{ color: item.color, backgroundColor: item.background }"
              :data-cy="`legendEntryRange-${item.level}`"
            >
              {{ item.min }}&ndash;{{ item.max }}
            </div>
          </div>
        </div>
      </v-card>
    </v-expand-transition>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useDisplay } from 'vuetify'
import { aqiTypes, aqiTypeKeys } from '@/store/map/utils'
import { AQICodes, IndiaAQICodes, type AQICode } from '@/services/map'
import { NAMESPACES } from '@/store/types'
import { MAP_GETTERS } from '@/store/map'

export default defineComponent({
  name: 'MapLegend',
  setup() {
    const { xs } = useDisplay()
    return { xs }
  },
  props: {
    isWidget: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      // should be closed by default when on mobile or inside a widget
      open: !this.xs && !this.isWidget
    }
  },
  computed: {
    hideLegend(): boolean {
      return this.$store.state.map.movingMode
    },
    aqiType(): number {
      return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_AQI_TYPE}`]
    },
    aqiTypeKey(): string {
      return aqiTypeKeys[this.aqiType as 0 | 1]
    },
    measurementType(): number {
      return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_MEASUREMENT_TYPE}`]
    },
    legendTitle(): string {
      const parts = [this.$t('map.legend.title') as string]
      const key = this.aqiTypeKey
      if (key) {
        parts.unshift(this.$t(`map.mapSettings.aqiTypeOptions.${key}`) as string)
      }
      return parts.join(' ')
    },
    legendItems(): AQICode[] {
      const codeMap = this.aqiType === aqiTypes.in ? IndiaAQICodes[this.measurementType] : AQICodes
      return codeMap.sort((a, b) => a.min - b.min)
    }
  },
  methods: {
    toggle(): void {
      this.open = !this.open
    },
    getLegendTitle(level: number): string {
      const key = this.aqiTypeKey
      return this.$t(`map.popover.descriptions.${key}.aqi_${level}.title`) as string
    }
  }
})
</script>

<style lang="scss" scoped>
$max-breakpoint: 900px;
$min-breakpoint: 901px;
$card-width: 280px;

.legend-card {
  width: $card-width;
}

.card-subtitle {
  height: 44px;
  font-weight: 400;
  color: #000;
  line-height: 44px;
  cursor: pointer;
  letter-spacing: 0.175px;
}

.category-subtitle {
  height: 30px;
  color: #000;
  font-weight: 500;
  letter-spacing: 0.0125rem;
  font-size: 12px;
  line-height: 14px;
  cursor: pointer;
}

.no-bottom-border-radius {
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}
.no-top-border-radius {
  border-top-left-radius: 0 !important;

  @media (max-width: $max-breakpoint) {
    border-top-right-radius: 0 !important;
  }

  .widget & {
    border-top-right-radius: 0 !important;
  }
}
.bottom-card {
  @media (max-width: $max-breakpoint) {
    width: $card-width;
    .hide-small {
      display: none;
    }
  }
  @media (min-width: $min-breakpoint) {
    .hide-large {
      display: none;
    }
  }

  .widget & {
    width: $card-width;
    .hide-small {
      display: none;
    }
  }

  .subtitle-large {
    font-size: 12px !important;
    margin-left: 16px;
  }
}
.legend {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  width: 750px;
  padding: 8px 16px;

  @media (max-width: $max-breakpoint) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0 12px;
    width: $card-width;
    padding-top: 0px;
    padding-bottom: 16px;
  }
  .widget & {
    grid-template-columns: repeat(2, 1fr);
    gap: 0 12px;
    width: $card-width;
    padding-top: 0px;
    padding-bottom: 16px;
  }
  .subtitle {
    font-size: 12px !important;
    line-height: 12px;
    padding-top: 24px;
    height: 50px;
  }
  .entry {
    line-height: 1.5;

    .category-subtitle {
      font-size: 12px !important;
      line-height: 14px;
      height: 30px;
      padding-bottom: 2px;
    }
    .range {
      font-size: 14px;
      width: 100%;
      padding-left: 4px;
      padding-right: 4px;
      font-weight: 500;
    }
    &.unhealthy_sensitive {
      .category-subtitle {
        @media (max-width: $max-breakpoint) {
          margin-top: 12px;
        }
        .widget & {
          margin-top: 12px;
        }
      }
    }
    &.unhealthy {
      .category-subtitle {
        @media (max-width: $max-breakpoint) {
          margin-top: 12px;
        }
        .widget & {
          margin-top: 12px;
        }
      }
    }
  }
}
</style>
